import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_ORDER = ["A", "L", "I", "G", "N"];

export default function Assessment() {
  const [questions, setQuestions] = useState(null);
  const [step, setStep] = useState(0); // 0 = lead form, 1..5 = pillars, 6 = submitting/loading
  const [lead, setLead] = useState({ name: "", work_email: "", company: "", role: "" });
  const [consent, setConsent] = useState(false);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/assessment/questions`).then((r) => setQuestions(r.data)).catch(() => setError("Could not load assessment."));
  }, []);

  const setScore = (key, value) => setResponses((r) => ({ ...r, [key]: value }));

  const currentPillar = step >= 1 && step <= 5 ? PILLAR_ORDER[step - 1] : null;
  const totalAnswered = Object.keys(responses).length;
  const progress = step === 0 ? 0 : Math.round((totalAnswered / 15) * 100);

  const canAdvancePillar = currentPillar
    ? [1, 2, 3].every((i) => responses[`${currentPillar}${i}`] !== undefined)
    : false;

  const canStart =
    lead.name.trim() && lead.work_email.includes("@") && lead.company.trim() && lead.role.trim() && consent;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/assessment/submit`, { lead, responses });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong generating your report.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ReportView result={result} lead={lead} onRetake={() => { setResult(null); setStep(0); setResponses({}); }} navigate={navigate} />;
  }

  return (
    <main data-testid="page-assessment" className="pt-20 bg-[var(--bone)] min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border" style={{ background: "rgba(201,164,90,0.10)", borderColor: "rgba(201,164,90,0.50)" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--accent-gold)" }}></span>
            <span className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--accent-gold-soft)" }}>
              Free · 5-Minute Diagnostic
            </span>
          </div>
          <h1 className="font-display text-[40px] md:text-[58px] leading-[1.02] mt-6">
            A.L.I.G.N.™ Readiness Index
          </h1>
          <p className="mt-5 max-w-2xl text-[16px] text-[var(--navy-100)]/85 leading-relaxed">
            Fifteen quick questions across five pillars. You'll receive a scored snapshot
            with your strongest pillar, weakest pillar, operational drag estimate,
            and a recommended intervention — written specifically for your
            organization by our A.L.I.G.N. diagnostic engine.
          </p>

          {/* Explicit free-vs-paid disambiguation — prevents readers from mistaking this
              free tool for the paid Organizational Systems Assessment. */}
          <div
            data-testid="readiness-vs-osa-disclaimer"
            className="mt-7 max-w-2xl p-5 border rounded-sm"
            style={{ background: "rgba(201,164,90,0.06)", borderColor: "rgba(201,164,90,0.30)" }}
          >
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "var(--accent-gold-soft)" }}>
              Please Note
            </div>
            <p className="mt-3 text-[13px] leading-[1.7] text-[var(--navy-100)]/85">
              The Readiness Index is a <strong className="text-[var(--bone)]">complimentary, high-level snapshot</strong> —
              not the full <strong className="text-[var(--bone)]">A.L.I.G.N.™ Organizational Systems Assessment</strong>{" "}
              (our paid, in-depth engagement that begins every client relationship). Use this to
              get an executive read on where alignment is breaking down, then decide whether a
              deeper, scoped diagnostic is the right next step.
            </p>
          </div>

          {/* progress */}
          <div className="mt-10">
            <div className="flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[var(--gold-soft)]">
              <span>{step === 0 ? "Step 1 · Identify" : `Pillar ${step}/5 · ${questions?.[currentPillar]?.name || ""}`}</span>
              <span>{progress}%</span>
            </div>
            <div className="mt-3 h-[2px] bg-white/15 overflow-hidden">
              <div className="h-full bg-[var(--gold)] transition-all duration-500" style={{ width: `${Math.max(progress, step === 0 ? 4 : 0)}%` }}></div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        {error && (
          <div data-testid="assessment-error" className="mb-6 p-4 border border-red-300 bg-red-50 text-red-800 text-[14px]">
            {error}
          </div>
        )}

        {step === 0 && (
          <div data-testid="lead-form" className="rise rise-1">
            <div className="eyebrow">Before We Begin</div>
            <h2 className="font-display text-[30px] md:text-[38px] text-[var(--navy-900)] mt-3">
              Where Should We Send Your Report?
            </h2>
            <p className="mt-3 text-[15px] text-[var(--muted)]">
              Your report will be emailed to you the moment it's generated. We never share your information.
            </p>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              <Field label="Full name" testid="lead-name">
                <input
                  data-testid="input-name"
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[var(--navy-900)]/25 focus:border-[var(--navy-900)] outline-none py-3 text-[16px] text-[var(--navy-900)]"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Work email" testid="lead-email">
                <input
                  data-testid="input-email"
                  type="email"
                  value={lead.work_email}
                  onChange={(e) => setLead({ ...lead, work_email: e.target.value })}
                  className="w-full bg-transparent border-b border-[var(--navy-900)]/25 focus:border-[var(--navy-900)] outline-none py-3 text-[16px] text-[var(--navy-900)]"
                  placeholder="jane@company.com"
                />
              </Field>
              <Field label="Company" testid="lead-company">
                <input
                  data-testid="input-company"
                  value={lead.company}
                  onChange={(e) => setLead({ ...lead, company: e.target.value })}
                  className="w-full bg-transparent border-b border-[var(--navy-900)]/25 focus:border-[var(--navy-900)] outline-none py-3 text-[16px] text-[var(--navy-900)]"
                  placeholder="Acme, Inc."
                />
              </Field>
              <Field label="Role / Title" testid="lead-role">
                <input
                  data-testid="input-role"
                  value={lead.role}
                  onChange={(e) => setLead({ ...lead, role: e.target.value })}
                  className="w-full bg-transparent border-b border-[var(--navy-900)]/25 focus:border-[var(--navy-900)] outline-none py-3 text-[16px] text-[var(--navy-900)]"
                  placeholder="Chief Operating Officer"
                />
              </Field>
            </div>

            {/* Consent + privacy reassurance */}
            <div className="mt-10 p-5 bg-[var(--navy-50)] border border-[var(--navy-900)]/8 text-[12px] leading-[1.65] text-[var(--muted)] max-w-2xl">
              <strong className="text-[var(--navy-900)]">Your responses are confidential.</strong>{" "}
              Your A.L.I.G.N.™ Readiness Index is generated by Claude AI based on your answers and reviewed only by authorized members of the Freedom In Me team. We do not sell, share, or rent your information. See our{" "}
              <Link to="/privacy" className="underline underline-offset-2 text-[var(--navy-900)] hover:text-[var(--gold)]">Privacy Policy</Link>.
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer max-w-2xl" data-testid="assessment-consent-wrapper">
              <input
                type="checkbox"
                data-testid="assessment-consent"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[var(--navy-900)] cursor-pointer flex-shrink-0"
              />
              <span className="text-[12px] leading-[1.6] text-[var(--navy-900)]">
                I have read and agree to the{" "}
                <Link to="/privacy" className="underline underline-offset-2 hover:text-[var(--gold)]">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/terms" className="underline underline-offset-2 hover:text-[var(--gold)]">Terms of Use</Link>. I understand the report is AI-generated and is for executive reflection — not professional advice.
              </span>
            </label>

            <button
              data-testid="lead-start-btn"
              disabled={!canStart}
              onClick={() => setStep(1)}
              className="btn-primary mt-12 px-8 py-4 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Begin Assessment <ArrowRight size={16} />
            </button>
          </div>
        )}

        {currentPillar && questions && (
          <div data-testid={`pillar-step-${currentPillar}`} className="rise rise-1" key={currentPillar}>
            <div className="flex items-baseline gap-5">
              <div className="font-display text-[88px] text-[var(--navy-900)] leading-none">{currentPillar}</div>
              <div>
                <div className="eyebrow">Pillar {step} of 5</div>
                <div className="font-display text-[28px] text-[var(--navy-900)] mt-1">{questions[currentPillar].name}</div>
                {questions[currentPillar].system && (
                  <div className="text-[12px] tracking-[0.18em] uppercase text-[var(--muted)] font-medium mt-1">
                    The {questions[currentPillar].system} system
                  </div>
                )}
              </div>
            </div>
            <p className="mt-6 text-[14px] text-[var(--muted)]">
              Rate each statement from 1 (strongly disagree) to 10 (strongly agree).
            </p>

            <div className="mt-10 space-y-10">
              {questions[currentPillar].questions.map((q, i) => {
                const key = `${currentPillar}${i + 1}`;
                const val = responses[key];
                return (
                  <div key={key} data-testid={`question-${key}`}>
                    <div className="flex items-start gap-4">
                      <span className="font-display text-[20px] text-[var(--gold)] leading-none mt-1">0{i + 1}</span>
                      <div className="flex-1 text-[17px] leading-relaxed text-[var(--navy-900)]">{q}</div>
                    </div>
                    <div className="mt-4 ml-9 grid grid-cols-10 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button
                          key={n}
                          data-testid={`score-${key}-${n}`}
                          onClick={() => setScore(key, n)}
                          className={`aspect-square text-[14px] font-medium border transition-all ${
                            val === n
                              ? "bg-[var(--navy-900)] text-[var(--bone)] border-[var(--navy-900)]"
                              : "bg-white border-[rgba(10,31,68,0.15)] text-[var(--navy-900)] hover:border-[var(--navy-900)]"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="ml-9 mt-2 flex justify-between text-[11px] text-[var(--muted)]">
                      <span>Strongly disagree</span><span>Strongly agree</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-14 flex items-center justify-between">
              <button
                data-testid="pillar-back-btn"
                onClick={() => setStep(step - 1)}
                disabled={step <= 1}
                className="text-[12px] tracking-[0.22em] uppercase font-semibold text-[var(--navy-900)] inline-flex items-center gap-2 disabled:opacity-30"
              >
                <ArrowLeft size={16} /> Back
              </button>

              {step < 5 ? (
                <button
                  data-testid="pillar-next-btn"
                  disabled={!canAdvancePillar}
                  onClick={() => { setStep(step + 1); window.scrollTo({top:0,behavior:'smooth'}); }}
                  className="btn-primary px-8 py-3.5 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  data-testid="pillar-submit-btn"
                  disabled={!canAdvancePillar || submitting}
                  onClick={submit}
                  className="btn-gold px-8 py-3.5 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                >
                  {submitting ? (<><Loader2 size={16} className="animate-spin" /> Generating Report</>) : (<>Generate Report <ArrowRight size={16} /></>)}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="block text-[10px] tracking-[0.28em] uppercase text-[var(--muted)] font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}

function ReportView({ result, lead, onRetake, navigate }) {
  const { scores, report, maturity_label, email_sent } = result;
  const pillars = scores.pillars;
  const sorted = Object.entries(pillars).sort((a, b) => a[1].score - b[1].score);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];

  return (
    <main data-testid="assessment-report" className="pt-20 bg-[var(--bone)] min-h-screen">
      <section className="relative overflow-hidden bg-[var(--navy-950)] text-[var(--bone)]">
        <div className="grain absolute inset-0"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full" style={{background:"radial-gradient(circle, rgba(201,164,90,0.2), transparent 60%)"}}/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow-light">A.L.I.G.N.™ Executive Diagnostic</div>
          <div className="font-display text-[18px] text-[var(--navy-100)]/80 mt-4">Prepared for {lead.name} · {lead.company}</div>

          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="font-display text-[96px] md:text-[140px] leading-none text-[var(--bone)]">
                {scores.overall}<span className="text-[40px] md:text-[56px] text-[var(--navy-100)]/50">/100</span>
              </div>
              <div className="text-[12px] tracking-[0.28em] uppercase text-[var(--gold-soft)] font-bold mt-2">{maturity_label}</div>
              <div className="font-display text-[24px] md:text-[30px] leading-tight mt-6 max-w-xl">{report.headline}</div>
            </div>
            <div className="md:col-span-5 space-y-4">
              {Object.entries(pillars).map(([k, p]) => (
                <div key={k} data-testid={`score-bar-${k}`}>
                  <div className="flex justify-between items-baseline">
                    <span className="font-display text-[20px] text-[var(--bone)]">{k} · <span className="text-[12px] tracking-[0.22em] uppercase text-[var(--navy-100)]/70 ml-1">{p.name}</span></span>
                    <span className="font-display text-[18px] text-[var(--gold-soft)]">{p.score}</span>
                  </div>
                  <div className="mt-2 h-[3px] bg-white/10 overflow-hidden">
                    <div className="h-full bg-[var(--gold)]" style={{ width: `${p.score}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 space-y-14">
        {email_sent ? (
          <div className="flex items-center gap-3 text-[14px] text-[var(--navy-700)]">
            <CheckCircle2 size={18} className="text-[var(--gold)]" />
            We've also emailed this report to {lead.work_email}.
          </div>
        ) : (
          <div className="text-[13px] text-[var(--muted)]">
            Your report is shown below. (Email delivery requires our sender domain to be verified — we've saved your submission and Donja will follow up directly.)
          </div>
        )}

        <Block label="Executive Summary">
          <p className="text-[17px] leading-[1.7] text-[var(--navy-900)] whitespace-pre-line">{report.executive_summary}</p>
        </Block>

        <div className="grid md:grid-cols-2 gap-10">
          <Block label="Strongest Pillar">
            <div className="font-display text-[44px] text-[var(--navy-900)] leading-none">{strongest[0]} <span className="text-[20px] text-[var(--muted)]">· {strongest[1].name}</span></div>
            <div className="text-[14px] text-[var(--gold)] mt-2 tracking-[0.22em] uppercase font-semibold">{strongest[1].score}/100</div>
          </Block>
          <Block label="Weakest Pillar">
            <div className="font-display text-[44px] text-[var(--navy-900)] leading-none">{weakest[0]} <span className="text-[20px] text-[var(--muted)]">· {weakest[1].name}</span></div>
            <div className="text-[14px] text-[var(--gold)] mt-2 tracking-[0.22em] uppercase font-semibold">{weakest[1].score}/100</div>
          </Block>
        </div>

        <Block label="Key Friction Areas">
          <ul className="space-y-3 mt-2">
            {(report.key_friction_areas || []).map((f, i) => (
              <li key={`friction-${i}-${typeof f === "string" ? f.slice(0, 32) : i}`} className="flex gap-3 text-[16px] text-[var(--navy-900)]">
                <span className="text-[var(--gold)] font-display text-[18px] leading-none mt-1">·</span> {f}
              </li>
            ))}
          </ul>
        </Block>

        <div className="grid md:grid-cols-2 gap-10">
          <Block label="Operational Drag Estimate"><p className="text-[16px] leading-relaxed text-[var(--navy-900)]">{report.operational_drag_estimate}</p></Block>
          <Block label="Retention Risk"><div className="font-display text-[44px] text-[var(--navy-900)] leading-none">{report.retention_risk}</div></Block>
        </div>

        <Block label="Modernization Readiness">
          <p className="text-[16px] leading-relaxed text-[var(--navy-900)]">{report.modernization_readiness}</p>
        </Block>

        <Block label="Pillar Insights">
          <div className="space-y-4 mt-2">
            {Object.entries(report.pillar_insights || {}).map(([k, v]) => (
              <div key={k} className="flex gap-5">
                <div className="font-display text-[28px] text-[var(--gold)] leading-none w-8">{k}</div>
                <div className="text-[15px] text-[var(--navy-900)] leading-relaxed">{v}</div>
              </div>
            ))}
          </div>
        </Block>

        <div className="bg-[var(--navy-950)] text-[var(--bone)] p-10 md:p-14">
          <div className="eyebrow-light">Recommended Intervention</div>
          <div className="font-display text-[28px] md:text-[36px] leading-tight mt-4">{report.recommended_intervention}</div>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="https://www.itsthefreedominme.com" target="_blank" rel="noreferrer" data-testid="report-cta-book" className="btn-gold px-8 py-4 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-2">
              Schedule Executive Diagnostic <ArrowUpRight size={14} />
            </a>
            <Link to="/align" data-testid="report-cta-framework" className="px-8 py-4 rounded-full text-[12px] tracking-[0.22em] uppercase font-semibold border border-[var(--bone)]/40 hover:border-[var(--gold-soft)] hover:text-[var(--gold-soft)] inline-flex items-center gap-2 transition-colors">
              Revisit the Framework
            </Link>
          </div>
        </div>

        <div className="text-center pt-4">
          <button onClick={onRetake} data-testid="retake-btn" className="link-underline text-[13px]">Retake the assessment</button>
        </div>

        {/* AI-generated content disclaimer — required for any AI output */}
        <div data-testid="report-ai-disclaimer" className="mt-6 p-6 bg-[var(--navy-50)] border border-[var(--navy-900)]/10 text-[12px] leading-[1.7] text-[var(--muted)]">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--navy-900)] font-bold">
            About this report
          </div>
          <p className="mt-2">
            This A.L.I.G.N.™ Readiness Index report is generated by Claude AI (Anthropic, PBC)
            based on the responses you provided and is intended for executive reflection only.
            It is <strong className="text-[var(--navy-900)]">not</strong> professional, legal,
            financial, medical, or fiduciary advice and may contain inaccuracies. Significant
            business decisions should be confirmed with a member of the Freedom In Me team or
            with your own qualified advisors. See our{" "}
            <Link to="/terms" className="underline underline-offset-2 text-[var(--navy-900)] hover:text-[var(--gold)]">
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline underline-offset-2 text-[var(--navy-900)] hover:text-[var(--gold)]">
              Privacy Policy
            </Link>.
          </p>
        </div>
      </section>
    </main>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}
